import React, { useState, useEffect, ChangeEvent, useCallback } from 'react';
import {
    Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Select, MenuItem,
    FormControl, Card, CardContent, CardHeader, Dialog, DialogTitle, DialogContent,
    DialogActions, Pagination, CircularProgress, Box, Typography, Paper, SelectChangeEvent, Chip
} from '@mui/material';
import Loader from '../Loader';
import { useToast } from '../../hooks/useToast';
import { ToastNotification } from '../common/ToastNotification';
import { formatApiError } from '../../utils/errorFormatter';

// --- Типы и константы ---

// Поддержка и числовых и строковых значений state
const stateDisplayMap: { [key: string]: { label: string; color: 'default' | 'success' | 'error' | 'warning' } } = {
    '0': { label: 'Новая', color: 'default' },
    '1': { label: 'Заблокирована', color: 'error' },
    '2': { label: 'Активна', color: 'success' },
    'New': { label: 'Новая', color: 'default' },
    'Blocked': { label: 'Заблокирована', color: 'error' },
    'Active': { label: 'Активна', color: 'success' }
};

const stateFilterOptions = [
    { value: '', label: 'Все' },
    { value: '0', label: 'Новая' },
    { value: '1', label: 'Заблокирована' },
    { value: '2', label: 'Активна' }
];

interface CardItem {
    id: number;
    cardNo: string;
    state: number;
    info?: string;
    pin?: number;
    pinTryCount: number;
    groupId: number;
}

interface Group {
    id: number;
    name: string;
}

interface Filters {
    cardNo: string;
    state: string;
    groupId: string;
}

interface PagedResult<T> {
    items: T[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

// Хук для дебаунса
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

export const Cards: React.FC = () => {
    // --- Состояния ---
    const [cards, setCards] = useState<CardItem[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [filters, setFilters] = useState<Filters>({
        cardNo: '',
        state: '',
        groupId: ''
    });

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [refreshCounter, setRefreshCounter] = useState(0);

    const debouncedFilters = useDebounce(filters, 500);

    const [confirmationModal, setConfirmationModal] = useState(false);
    const [selectedCard, setSelectedCard] = useState<CardItem | null>(null);
    const [actionType, setActionType] = useState<'unblock' | 'reset-pin' | null>(null);

    const { toast, showError, showSuccess, handleClose } = useToast();

    // --- Функции для работы с API ---
    const fetchGroups = async () => {
        try {
            const response = await fetch('api/management/groups');
            if (response.ok) {
                const data = await response.json();
                setGroups(data);
            }
        } catch (error) {
            console.error('Failed to fetch groups:', error);
        }
    };

    const fetchCards = useCallback(async (isInitialLoad: boolean) => {
        if (isInitialLoad) {
            setLoading(true);
        } else {
            setTableLoading(true);
        }

        const params = new URLSearchParams();
        if (debouncedFilters.cardNo) params.append('cardNo', debouncedFilters.cardNo);
        if (debouncedFilters.state) params.append('state', debouncedFilters.state);
        if (debouncedFilters.groupId) params.append('groupId', debouncedFilters.groupId);
        params.append('pageNumber', pageNumber.toString());
        params.append('pageSize', pageSize.toString());

        try {
            const response = await fetch(`api/cards?${params.toString()}`);
            if (response.ok) {
                const pagedResult: PagedResult<CardItem> = await response.json();
                setCards(pagedResult.items);
                setTotalPages(pagedResult.totalPages || Math.ceil(pagedResult.totalCount / pageSize));
                setTotalCount(pagedResult.totalCount);
            } else {
                const errorMsg = await formatApiError(response, 'Не удалось загрузить карты');
                showError(errorMsg);
            }
        } catch (error: any) {
            showError('Произошла сетевая ошибка при загрузке карт: ' + (error.message || ''));
        } finally {
            if (isInitialLoad) {
                setLoading(false);
            } else {
                setTableLoading(false);
            }
        }
    }, [debouncedFilters, pageNumber, pageSize, showError]);

    const unblockCard = async (card: CardItem) => {
        try {
            const response = await fetch(`api/cards/${encodeURIComponent(card.cardNo)}/unblock?groupId=${card.groupId}`, { method: 'POST' });
            if (response.ok) {
                showSuccess('Карта успешно разблокирована');
                setRefreshCounter(c => c + 1);
            } else {
                const errorMsg = await formatApiError(response, 'Не удалось разблокировать карту');
                showError(errorMsg);
            }
        } catch (error: any) {
            showError('Произошла ошибка при разблокировке карты: ' + (error.message || ''));
        }
    };

    const resetPin = async (card: CardItem) => {
        try {
            const response = await fetch(`api/cards/${encodeURIComponent(card.cardNo)}/reset-pin?groupId=${card.groupId}`, { method: 'POST' });
            if (response.ok) {
                showSuccess('PIN-код успешно сброшен');
                setRefreshCounter(c => c + 1);
            } else {
                const errorMsg = await formatApiError(response, 'Не удалось сбросить PIN-код');
                showError(errorMsg);
            }
        } catch (error: any) {
            showError('Произошла ошибка при сбросе PIN-кода: ' + (error.message || ''));
        }
    };

    // --- Эффекты ---
    useEffect(() => {
        fetchGroups();
        fetchCards(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchCards(false);
    }, [debouncedFilters, pageNumber, refreshCounter, fetchCards]);

    // Сброс страницы при изменении фильтров
    useEffect(() => {
        setPageNumber(1);
    }, [debouncedFilters]);

    // --- Обработчики событий ---
    const handleFilterChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectFilterChange = (e: SelectChangeEvent<string>) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        setPageNumber(page);
    };

    const openConfirmation = (card: CardItem, action: 'unblock' | 'reset-pin') => {
        setSelectedCard(card);
        setActionType(action);
        setConfirmationModal(true);
    };

    const handleConfirm = async () => {
        if (selectedCard && actionType) {
            if (actionType === 'unblock') {
                await unblockCard(selectedCard);
            } else if (actionType === 'reset-pin') {
                await resetPin(selectedCard);
            }
        }
        setConfirmationModal(false);
        setSelectedCard(null);
        setActionType(null);
    };

    const getGroupName = (groupId: number) => {
        const group = groups.find(g => g.id === groupId);
        return group ? group.name : `Группа ${groupId}`;
    };

    // --- Рендеринг ---
    if (loading) {
        return <Loader />;
    }

    return (
        <Box>
            <ToastNotification toast={toast} onClose={handleClose} />

            <Card>
                <CardHeader title="Управление картами лояльности" />
                <CardContent>
                    <Box sx={{ position: 'relative' }}>
                        {tableLoading && (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                zIndex: 2
                            }}>
                                <CircularProgress />
                            </Box>
                        )}
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Номер карты</TableCell>
                                        <TableCell>Группа</TableCell>
                                        <TableCell>Статус</TableCell>
                                        <TableCell>Попыток PIN</TableCell>
                                        <TableCell>Комментарий</TableCell>
                                        <TableCell style={{ width: '200px' }}>Действия</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell></TableCell>
                                        <TableCell>
                                            <TextField
                                                size="small"
                                                variant="standard"
                                                name="cardNo"
                                                placeholder="Номер карты..."
                                                value={filters.cardNo}
                                                onChange={handleFilterChange}
                                                autoComplete="off"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <FormControl size="small" variant="standard" fullWidth>
                                                <Select name="groupId" value={filters.groupId} onChange={handleSelectFilterChange} displayEmpty>
                                                    <MenuItem value=""><em>Все группы</em></MenuItem>
                                                    {groups.map(group => (
                                                        <MenuItem key={group.id} value={group.id.toString()}>{group.name}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            <FormControl size="small" variant="standard" fullWidth>
                                                <Select name="state" value={filters.state} onChange={handleSelectFilterChange} displayEmpty>
                                                    {stateFilterOptions.map(option => (
                                                        <MenuItem key={option.value} value={option.value}>
                                                            {option.value === '' ? <em>{option.label}</em> : option.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cards.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                <Typography color="textSecondary">Карты не найдены</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        cards.map(card => (
                                            <TableRow key={card.id} hover>
                                                <TableCell>{card.id}</TableCell>
                                                <TableCell>{card.cardNo}</TableCell>
                                                <TableCell>{getGroupName(card.groupId)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={stateDisplayMap[card.state]?.label || 'Неизвестно'}
                                                        color={stateDisplayMap[card.state]?.color || 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>{card.pinTryCount}</TableCell>
                                                <TableCell>{card.info}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        {(card.state === 1 || card.state === 'Blocked' as any) && (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="success"
                                                                onClick={() => openConfirmation(card, 'unblock')}
                                                            >
                                                                Разблокировать
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="warning"
                                                            onClick={() => openConfirmation(card, 'reset-pin')}
                                                        >
                                                            Сбросить PIN
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {totalPages > 1 && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                            <Pagination count={totalPages} page={pageNumber} onChange={handlePageChange} color="primary" />
                        </Box>
                    )}
                    {totalCount > 0 && <Typography variant="caption" display="block" sx={{ mt: 1 }}>Всего записей: {totalCount}</Typography>}
                </CardContent>
            </Card>

            <Dialog open={confirmationModal} onClose={() => setConfirmationModal(false)}>
                <DialogTitle>Подтверждение действия</DialogTitle>
                <DialogContent>
                    {selectedCard && actionType && (
                        <Typography>
                            {actionType === 'unblock'
                                ? `Вы уверены, что хотите разблокировать карту ${selectedCard.cardNo}?`
                                : `Вы уверены, что хотите сбросить PIN-код для карты ${selectedCard.cardNo}? Клиенту будет предложено установить новый PIN при следующем входе.`
                            }
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirm} color="primary">Подтвердить</Button>
                    <Button onClick={() => setConfirmationModal(false)}>Отмена</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};